/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import type { CalendarDate } from '@internationalized/date';
import get from 'lodash/get';
import {
    createContext,
    ReactElement,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';
import { DateValue } from 'react-aria';
import { useDispatch, useSelector } from 'react-redux';
import { announce, calendarDateToUtcString, SearchActions } from 'sarsaparilla';
import { updateSelectedDate } from 'ui-search/src/actions/search';

import type { Campground } from '../../../types/camping';
import { fetchCampgroundAvailability } from '../../shared/actions/availability';
import { setGridRange } from '../../shared/actions/availabilityGrid';
import { NUMBER_OF_DAYS_DISPLAYED_AVAILABILITY_MAX } from '../../shared/constants';
import {
    formatMDYShort,
    getTodayISO,
    UTCStringToCalendarDate,
} from '../../shared/utils/dateUtils';

export type CampgroundPageOnDatesChange = ({
    startDate,
    endDate,
    refreshData,
}: {
    startDate: DateValue | null;
    endDate: DateValue | null;
    refreshData?: boolean;
}) => void;
interface ContextValue {
    onDatesChange: CampgroundPageOnDatesChange;
    LTSStartDate: CalendarDate | null;
    LTSEndDate: CalendarDate | null;
    setLTSStartDate: (date: CalendarDate | null) => void;
    setLTSEndDate: (date: CalendarDate | null) => void;
    isLongTermStay: boolean;
    isResponsiveModalOpen: boolean;
    setIsResponsiveModalOpen: (isOpen: boolean) => void;
}

const CampgroundPageContext = createContext<ContextValue>({} as ContextValue);

interface CampgroundPageProviderProps {
    children: ReactElement;
}

function CampgroundPageProvider({ children }: CampgroundPageProviderProps) {
    const dispatch = useDispatch<any>();
    const [isResponsiveModalOpen, setIsResponsiveModalOpen] = useState<boolean>(false);
    const [LTSStartDate, setLTSStartDate] = useState<CalendarDate | null>(null);
    const [LTSEndDate, setLTSEndDate] = useState<CalendarDate | null>(null);
    const campground = useSelector<any, Campground>((state) =>
        get(state, 'campground.campground.results', {})
    );
    const isLongTermStay = campground.facility_rules?.allowRecurringPayment?.value === 1;

    const gridSize = useSelector<any, number>((state) =>
        get(state, 'availabilityGrid.gridSize', NUMBER_OF_DAYS_DISPLAYED_AVAILABILITY_MAX)
    );
    const gridStartDate = useSelector<any, string>((state) =>
        get(state, 'availabilityGrid.gridStartDate', getTodayISO())
    );
    const gridEndDate = useSelector<any, string>((state) =>
        get(state, 'availabilityGrid.gridEndDate')
    );

    const onDatesChange = useCallback(
        ({
            startDate,
            endDate,
            refreshData = false,
        }: {
            startDate: DateValue | null;
            endDate: DateValue | null;
            refreshData?: boolean;
        }) => {
            if (!startDate || !endDate) {
                announce(
                    'Please enter a check in and check out date to view available sites'
                );
                dispatch(updateSelectedDate(null, true));
                dispatch(updateSelectedDate(null, false));
                return;
            }

            // Validate dates are from this year or later
            const currentYear = new Date().getFullYear();
            if (startDate && startDate.year < currentYear) return;
            if (endDate && endDate.year < currentYear) return;

            announce(`Your check in date of ${formatMDYShort(startDate)} and check out date of ${formatMDYShort(endDate)}
                have been input successfully. Availability updated.`);

            dispatch(updateSelectedDate(formatMDYShort(startDate), true));
            dispatch(updateSelectedDate(formatMDYShort(endDate), false));

            const gridStartDateValue =
                startDate || UTCStringToCalendarDate(gridStartDate);

            dispatch(
                setGridRange({
                    startDate: calendarDateToUtcString(gridStartDateValue) || '',
                    endDate:
                        calendarDateToUtcString(
                            gridStartDateValue?.add({ days: gridSize })
                        ) || '',
                })
            );

            dispatch(
                fetchCampgroundAvailability({
                    startDate: calendarDateToUtcString(startDate) as string,
                    endDate: calendarDateToUtcString(endDate),
                    shouldRefresh: refreshData,
                })
            );

            SearchActions.trackRunSearch(
                {
                    category: 'Camping',
                    action: `${formatMDYShort(startDate)} to ${formatMDYShort(endDate)}`,
                    label: 'filters bar',
                },
                () => {}
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatch, gridEndDate, gridStartDate, gridSize]
    );

    const value = useMemo(
        () => ({
            onDatesChange,
            LTSStartDate,
            setLTSStartDate,
            LTSEndDate,
            setLTSEndDate,
            isLongTermStay,
            isResponsiveModalOpen,
            setIsResponsiveModalOpen,
        }),
        [onDatesChange, LTSStartDate, LTSEndDate, isLongTermStay, isResponsiveModalOpen]
    );

    return (
        <CampgroundPageContext.Provider value={value}>
            {children}
        </CampgroundPageContext.Provider>
    );
}

export const useCampgroundPageContext = () => {
    const context = useContext(CampgroundPageContext);
    return context;
};

export default CampgroundPageProvider;
